import { useState, type ChangeEvent, type FormEvent } from 'react'
import { api, errorText } from '../../api.ts'
import { SOURCE_TYPES, useJobs, useJobsAction } from '../../jobs.ts'
import type { JobColumns, JobsDetail, JobSource, MailEntry } from '../../types.ts'
import { Badge, Field, Loading, Notice, when } from '../../ui.tsx'
import { Button, buttonClass } from '../../components/Button.tsx'

/** Setting up a company's job sources (PLAN.md 1.6, 1.7): Icefield administrators only. The
 *  DASH mailbox and Smartsheet connectors themselves are P1-06. Until then, and as a
 *  fallback, a report can be uploaded here. */
export function JobSources({ companyId }: { companyId: string }) {
  const jobs = useJobs(companyId)
  if (jobs.isPending) return <Loading />
  if (jobs.isError) return <Notice>{errorText(jobs.error)}</Notice>
  return (
    <section>
      {jobs.data.sources.map((s) => (
        <div key={s.id}>
          <SourceCard companyId={companyId} source={s} rowIdColumn={jobs.data.rowIdColumn} />
          {s.type === 'dash_email' && (
            <MailCard companyId={companyId} source={s} mailbox={jobs.data.mailbox} entries={jobs.data.mail.filter((m) => m.sourceId === s.id)} />
          )}
        </div>
      ))}
      <NewSource companyId={companyId} />
    </section>
  )
}

const COLUMN_FIELDS: Array<[keyof JobColumns, string, string]> = [
  ['key', 'Key column', 'What identifies a job (R1). DASH: the job number. Smartsheet: the row id.'],
  ['number', 'Job number column', ''],
  ['name', 'Job name column', ''],
  ['status', 'Status column', ''],
  ['pm', 'Project manager column', 'Optional'],
  ['address', 'Address column', 'Optional'],
]

const mapToText = (m: Record<string, string>) =>
  Object.entries(m).map(([value, status]) => `${value} = ${status}`).join('\n')

function textToMap(text: string): Record<string, 'open' | 'closed'> {
  const map: Record<string, 'open' | 'closed'> = {}
  for (const line of text.split('\n')) {
    if (!line.trim()) continue
    const [value, status] = line.split('=').map((s) => s.trim())
    if (!value || (status !== 'open' && status !== 'closed')) throw new Error(`"${line.trim()}" should look like: In Progress = open`)
    map[value] = status
  }
  return map
}

function SourceCard({ companyId, source, rowIdColumn }: { companyId: string; source: JobSource; rowIdColumn: string }) {
  const act = useJobsAction(companyId)
  const [name, setName] = useState(source.name)
  const [enabled, setEnabled] = useState(source.enabled)
  const [staleAfter, setStaleAfter] = useState(source.staleAfterMinutes)
  const [columns, setColumns] = useState<JobColumns>(source.columns)
  const [statusMap, setStatusMap] = useState(mapToText(source.statusMap))
  const [message, setMessage] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null)

  const save = (e: FormEvent) => {
    e.preventDefault()
    setMessage(null)
    let map
    try {
      map = textToMap(statusMap)
    } catch (err) {
      return setMessage({ kind: 'error', text: errorText(err) })
    }
    act.mutate(
      () =>
        api.patch<JobsDetail>(`/admin/companies/${companyId}/job-sources/${source.id}`, {
          version: source.version,
          name,
          enabled,
          staleAfterMinutes: staleAfter,
          columns,
          statusMap: map,
        }),
      {
        onSuccess: () => setMessage({ kind: 'ok', text: 'Saved.' }),
        onError: (err) => setMessage({ kind: 'error', text: errorText(err) }),
      },
    )
  }

  return (
    <div className="card">
      <h2>
        {source.name} <span className="muted small">{SOURCE_TYPES[source.type]}</span> {!source.enabled && <Badge tone="warn">Off</Badge>}
      </h2>
      <p className="small">
        Last good report: {source.lastGoodImportAt ? when(source.lastGoodImportAt) : 'none yet'}. Report time of the last one applied:{' '}
        {source.lastReportTime ? when(source.lastReportTime) : '—'}.
      </p>
      <Upload companyId={companyId} source={source} />
      <details>
        <summary>Settings</summary>
        <form className="form" onSubmit={save}>
          <Notice kind={message?.kind}>{message?.text}</Notice>
          <Field label="Name">
            <input required value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <label className="check">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            On: reports are checked for being late (R13)
          </label>
          <Field label="Alert if no good report for (minutes)" hint="R13. DASH: 180. Smartsheet: 60.">
            <input type="number" min={5} value={staleAfter} onChange={(e) => setStaleAfter(Number(e.target.value))} />
          </Field>
          <fieldset>
            <legend>Columns in the report</legend>
            {COLUMN_FIELDS.map(([field, label, hint]) => (
              <Field key={field} label={label} hint={field === 'key' && source.type === 'smartsheet' ? `${hint} Use ${rowIdColumn}.` : hint}>
                <input
                  required={field !== 'pm' && field !== 'address'}
                  value={columns[field] ?? ''}
                  onChange={(e) => setColumns({ ...columns, [field]: e.target.value })}
                />
              </Field>
            ))}
          </fieldset>
          <Field label="Status values" hint="One per line, as: In Progress = open. Anything not listed counts as open, and is flagged (R4).">
            <textarea rows={6} value={statusMap} onChange={(e) => setStatusMap(e.target.value)} />
          </Field>
          <div className="form-actions">
            <Button type="submit" disabled={act.isPending}>Save</Button>
          </div>
        </form>
      </details>
    </div>
  )
}

function Upload({ companyId, source }: { companyId: string; source: JobSource }) {
  const act = useJobsAction(companyId)
  const [result, setResult] = useState<string | null>(null)

  const upload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setResult(null)
    act.mutate(
      async () => {
        const detail = await uploadReport(companyId, source.id, file)
        const latest = detail.imports.find((i) => i.id === detail.importId)
        setResult(latest ? `${file.name}: ${latest.state}${latest.error ? ` (${latest.error})` : ''}` : `${file.name}: queued`)
        return detail
      },
      { onError: (err) => setResult(errorText(err)) },
    )
  }

  return (
    <div className="row upload">
      <label className={buttonClass({ variant: 'secondary' })}>
        Upload a report (CSV)
        <input type="file" accept=".csv,text/csv" hidden disabled={act.isPending} onChange={upload} />
      </label>
      {result && <span className="small">{result}</span>}
    </div>
  )
}

async function uploadReport(companyId: string, sourceId: string, file: File): Promise<JobsDetail & { importId: string }> {
  const res = await fetch(`/dashboard/api/admin/companies/${companyId}/job-sources/${sourceId}/upload`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-ICE-Dashboard': '1',
      'Content-Type': 'text/csv',
      'X-File-Name': encodeURIComponent(file.name),
      'X-Report-Time': new Date().toISOString(),
    },
    body: file,
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data?.error?.message ?? `Upload failed (${res.status})`)
  return data
}

function NewSource({ companyId }: { companyId: string }) {
  const act = useJobsAction(companyId)
  const [type, setType] = useState<JobSource['type']>('dash_email')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  return (
    <form
      className="card form"
      onSubmit={(e) => {
        e.preventDefault()
        setError(null)
        act.mutate(() => api.post<JobsDetail>(`/admin/companies/${companyId}/job-sources`, { type, name }), {
          onSuccess: () => setName(''),
          onError: (err) => setError(errorText(err)),
        })
      }}
    >
      <h2>Add a job source</h2>
      <Notice>{error}</Notice>
      <Field label="Kind">
        <select value={type} onChange={(e) => setType(e.target.value as JobSource['type'])}>
          {Object.entries(SOURCE_TYPES).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </Field>
      <Field label="Name" hint="As people will see it, such as DASH or HIBCO jobs sheet.">
        <input required value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <p className="muted small">It starts with the usual column titles and status values. Set the real ones from the client's sample report.</p>
      <div className="form-actions">
        <Button type="submit" disabled={act.isPending}>Add</Button>
      </div>
    </form>
  )
}

/** Where DASH emails this company's reports, and how ICE checks them (P1-06). */
function MailCard({ companyId, source, mailbox, entries }: { companyId: string; source: JobSource; mailbox: JobsDetail['mailbox']; entries: MailEntry[] }) {
  const act = useJobsAction(companyId)
  const mail = source.mail
  const [address, setAddress] = useState(mail?.address ?? '')
  const [senders, setSenders] = useState((mail?.senders ?? []).join('\n'))
  const [attachment, setAttachment] = useState(mail?.attachment ?? '*.csv')
  const [requireAuthentication, setRequireAuthentication] = useState(mail?.requireAuthentication ?? true)
  const [message, setMessage] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null)

  const save = (e: FormEvent) => {
    e.preventDefault()
    setMessage(null)
    act.mutate(
      () =>
        api.patch<JobsDetail>(`/admin/companies/${companyId}/job-sources/${source.id}`, {
          version: source.version,
          mail: address.trim()
            ? { address, senders: senders.split(/[\s,]+/).filter(Boolean), attachment, requireAuthentication }
            : null,
        }),
      {
        onSuccess: () => setMessage({ kind: 'ok', text: 'Saved.' }),
        onError: (err) => setMessage({ kind: 'error', text: errorText(err) }),
      },
    )
  }

  return (
    <div className="card">
      <h2>Email</h2>
      {mailbox ? (
        <>
          <p className="small">
            The job worker reads <strong>{mailbox.address}</strong> every five minutes, and last did {when(mailbox.polledAt)}. Reports for{' '}
            {source.name} are the messages sent to the address below.
          </p>
          {mailbox.lastError && <Notice>The last read failed: {mailbox.lastError}</Notice>}
          {!mailbox.lastError && Date.now() - new Date(mailbox.polledAt).getTime() > 15 * 60_000 && (
            <Notice>The job worker hasn't read the mailbox for more than 15 minutes. Is it running, with its mailbox set up?</Notice>
          )}
        </>
      ) : (
        <Notice kind="info">The job worker hasn't read a mailbox yet. It needs its JOB_MAIL_* settings to read emailed reports.</Notice>
      )}
      <form className="form" onSubmit={save}>
        <Notice kind={message?.kind}>{message?.text}</Notice>
        <Field label="Report address" hint="The address DASH sends this company's report to: an alias on the mailbox, such as dash-fg@icefield.tech. Leave empty to stop reading email for this source.">
          <input type="email" value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        <Field label="Expected senders" hint="One per line: a whole address, or @domain for anyone at that domain. Mail from anyone else is refused.">
          <textarea rows={2} value={senders} onChange={(e) => setSenders(e.target.value)} />
        </Field>
        <Field label="Attachment name" hint="* and ? are wildcards. A message must carry exactly one such attachment.">
          <input value={attachment} onChange={(e) => setAttachment(e.target.value)} />
        </Field>
        <label className="check">
          <input type="checkbox" checked={requireAuthentication} onChange={(e) => setRequireAuthentication(e.target.checked)} />
          Require the sender's domain to vouch for the message (SPF, DKIM or DMARC). Leave on.
        </label>
        <div className="form-actions">
          <Button type="submit" disabled={act.isPending}>Save</Button>
        </div>
      </form>
      {entries.length > 0 && (
        <>
          <h3 className="small">Recent emails</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Received</th><th>From</th><th>Result</th></tr>
              </thead>
              <tbody>
                {entries.map((m) => (
                  <tr key={m.id}>
                    <td className="nowrap">{when(m.receivedAt)}</td>
                    <td className="small">{m.sender ?? '—'}</td>
                    <td className="small">
                      {m.outcome === 'queued' ? <Badge tone="ok">Imported</Badge> : <Badge tone="bad">Refused</Badge>}{' '}
                      {m.outcome === 'queued' ? m.attachmentName : m.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
